/**
 * Pure decision logic behind `PromptComposer` — the single composer used both on
 * the home screen and inside the global create dialog.
 *
 * The composer itself is a thin React shell around these helpers. Keeping the
 * rules here means the two surfaces provably behave identically (that duplicated,
 * silently drifting behaviour is exactly what the shared component replaces) and
 * that the rules stay unit-testable without a DOM.
 */

import { AUTO_BLUEPRINT_ID } from '#zero/mutators.ts'

/** Task ("implementation") vs. collaborative session. */
export type ComposerMode = 'implementation' | 'session'

export interface ComposerRepo {
  id: number
  default_branch?: string | null
}

/**
 * A model offered by the platform execution config.
 *
 * Narrowed to the one field the selection rules actually read, so the tRPC
 * `executionConfig.models` payload and test fixtures both satisfy it
 * structurally — no cast at either call site.
 */
export interface ComposerModel {
  id: string
}

export interface ComposerBlueprintStep {
  name: string
}

export interface ComposerBlueprint {
  id: string
  name?: string
  steps?: readonly ComposerBlueprintStep[]
}

/**
 * Blueprints offered in the composer picker.
 *
 * System catalog revisions can share a product-facing name (e.g. two "Feature"
 * rows for V2 vs V3). The list query only returns `active` rows, but a leaked
 * revision would still show as a duplicate label — keep the first of each name
 * in display order.
 */
export function uniqueBlueprintsByName<T extends { name: string }>(
  blueprints: readonly T[],
): T[] {
  const seen = new Set<string>()
  return blueprints.filter((blueprint) => {
    if (seen.has(blueprint.name)) return false
    seen.add(blueprint.name)
    return true
  })
}

/**
 * The repo selection to fall back to while the user has not picked one.
 *
 * The remembered repo id is stored per workspace and can outlive the repository
 * (revoked install, renamed org, repo removed from the GitHub App). Selecting an
 * id that is no longer in `repos` would leave the select showing a value the user
 * cannot see or change, so unknown ids resolve to "nothing selected".
 */
export function resolveDefaultRepoSelection(
  lastRepoId: string | null | undefined,
  repos: readonly ComposerRepo[],
): string[] {
  if (!lastRepoId) return []
  return repos.some((repo) => repo.id.toString() === lastRepoId)
    ? [lastRepoId]
    : []
}

/**
 * The model selection to fall back to while the user has not picked one:
 * the model they last *started* work with, else the platform default.
 *
 * The remembered id can outlive its model — a provider dropped from the
 * platform config, a retired model. Unlike a stale repo id, a stale model id is
 * not merely cosmetic: `isStartDisabled` only checks that *some* model id is
 * present, so preselecting an id the catalog no longer contains would leave the
 * picker showing nothing selectable while still submitting that dead id to the
 * server. Validating against `models` is what keeps the visible selection and
 * the submitted payload the same thing.
 *
 * "Still offered" means present in `models` — nothing more. A `deprecated`
 * model deliberately still counts: `ModelSelect` renders deprecated entries as
 * selectable, so filtering them here would silently disagree with the dropdown
 * the user is looking at. There is a test pinning this; it is not an oversight.
 *
 * Falling back to `defaultModelId` rather than to "nothing selected" (which is
 * what `resolveDefaultRepoSelection` does for repos) is deliberate: there is
 * always a sensible platform-wide model to start with, whereas there is no
 * sensible default repository.
 *
 * While the execution config query is in flight both `models` and
 * `defaultModelId` are empty, so this yields `[]` — the picker shows its
 * placeholder and `isStartDisabled` blocks the start. That is why the composer
 * *derives* this on every render instead of seeding state once in an effect:
 * the empty catalog is "not loaded yet", not "the remembered model is gone",
 * and a derived value corrects itself the moment the config arrives.
 */
export function resolveInitialModelSelection(
  lastModelId: string | null | undefined,
  defaultModelId: string,
  models: readonly ComposerModel[],
): string[] {
  if (lastModelId && models.some((model) => model.id === lastModelId)) {
    return [lastModelId]
  }
  return defaultModelId ? [defaultModelId] : []
}

/**
 * The **Default model** selection shown while the user has not explicitly
 * picked one: the user's configured default when it is still offered, else the
 * platform default.
 *
 * This is display-only inheritance — see `resolveSubmittedTaskModel` for why
 * an untouched inherited selection submits *no* task model. Validating the
 * stored user default against `models` mirrors the server's own resolution
 * (`resolveUserDefaultModel` re-checks the catalog), so what the picker shows
 * is exactly what a new run would resolve. While the execution config is still
 * loading both inputs are empty and this yields `[]`, which keeps the start
 * button disabled rather than submitting a dead id.
 */
export function resolveInheritedDefaultModelSelection(
  userDefaultModel: string | null | undefined,
  platformDefaultModelId: string,
  models: readonly ComposerModel[],
): string[] {
  if (
    userDefaultModel &&
    models.some((model) => model.id === userDefaultModel)
  ) {
    return [userDefaultModel]
  }
  return platformDefaultModelId ? [platformDefaultModelId] : []
}

/**
 * The `model` value to submit when starting a task.
 *
 * The task's stored model is an *explicit default*, not a force-all override:
 * leaving the inherited **Default model** untouched persists no task model, so
 * every new run of the task re-resolves the user's then-current default.
 * Only an explicit in-composer selection pins the task to that id. There is no
 * synthetic `Automatic` model value — inheritance is the absence of a model.
 */
export function resolveSubmittedTaskModel(input: {
  explicitlySelected: boolean
  selectedModelId: string | undefined
}): string | undefined {
  return input.explicitlySelected ? input.selectedModelId : undefined
}

/** The branch to select after switching to `repoId` — its default branch. */
export function defaultBranchSelection(
  repoId: string | undefined,
  repos: readonly ComposerRepo[],
): string[] {
  const repo = repos.find((candidate) => candidate.id.toString() === repoId)
  return repo?.default_branch ? [repo.default_branch] : []
}

/** A base branch, remembered together with the repo it was picked for. */
export interface BranchChoice {
  repoId: string
  branch: string
}

/**
 * The base branch to show and submit for the current repo selection.
 *
 * A picked branch is only valid for the repo it was picked on, so it is stored
 * with that repo's id rather than on its own. The primary repo can change
 * *without* going through the branch picker: the "last selected repository" is a
 * shared user setting, so a second composer (the create dialog open over the home
 * screen) can re-point it underneath this one. Keying the branch to its repo
 * means such a change falls back to "no override" — the select shows the new
 * repo's default branch as a placeholder — instead of displaying and submitting a
 * branch that does not exist on the newly selected repo.
 */
export function resolveSelectedBranch(
  choice: BranchChoice | null,
  selectedRepoIds: readonly string[],
): string[] {
  const repoId = selectedRepoIds[0]
  if (!choice || !repoId || choice.repoId !== repoId) return []
  return [choice.branch]
}

/**
 * The active blueprint id: the user's explicit pick when it still matches a
 * loaded blueprint, otherwise the first blueprint. Blueprints stream in via Zero,
 * so an empty list (first render) legitimately yields `''`.
 *
 * The `AUTO_BLUEPRINT_ID` sentinel is a static picker entry, not a member of the
 * streamed catalog, so it would fail the `blueprints.some(...)` membership check
 * and be collapsed to the first blueprint. Pass it through verbatim instead: the
 * server mutator resolves it to a concrete blueprint at start time.
 */
export function resolveBlueprintId(
  override: string | undefined,
  blueprints: readonly ComposerBlueprint[],
): string {
  if (override === AUTO_BLUEPRINT_ID) {
    return override
  }
  if (override && blueprints.some((bp) => bp.id === override)) {
    return override
  }
  return blueprints[0]?.id ?? ''
}

/**
 * Whether a blueprint has a plan step, i.e. whether "auto accept plan" is a
 * meaningful choice for it. Blueprints without one never pause for approval.
 */
export function blueprintHasPlanStep(
  blueprint: ComposerBlueprint | undefined,
): boolean {
  return blueprint?.steps?.some((step) => step.name === 'plan') ?? false
}

/**
 * Whether the "auto accept plan" switch is offered at all.
 *
 * Sessions have no blueprint to plan, and a blueprint without a plan step never
 * pauses for approval, so in both cases the switch would be a control that does
 * nothing.
 */
export function shouldShowAutoAcceptPlan(
  mode: ComposerMode,
  blueprint: ComposerBlueprint | undefined,
): boolean {
  return mode === 'implementation' && blueprintHasPlanStep(blueprint)
}

/**
 * Per-repo base branch overrides for `buildRepoSelection`.
 *
 * Only the single-repo case exposes a branch picker; with several repos selected
 * the picker is hidden and the backend resolves each repo's default branch, so a
 * stale branch from a previous single-repo selection must not leak into the payload.
 */
export function resolveBaseBranchById(
  selectedRepoIds: readonly string[],
  selectedBranch: readonly string[],
): Record<string, string> {
  const repoId = selectedRepoIds[0]
  const branch = selectedBranch[0]
  if (selectedRepoIds.length !== 1 || !repoId || !branch) return {}
  return { [repoId]: branch }
}

export interface StartRequirements {
  mode: ComposerMode
  prompt: string
  repoId: string | undefined
  modelId: string | undefined
  blueprintId: string
}

/**
 * Whether the submit button is disabled.
 *
 * A session opens an empty room the user can talk to afterwards, so its prompt is
 * optional. A task is dispatched to an agent immediately and never gets a second
 * chance at input, so it requires a non-blank prompt and a blueprint to run.
 */
export function isStartDisabled({
  mode,
  prompt,
  repoId,
  modelId,
  blueprintId,
}: StartRequirements): boolean {
  if (!repoId || !modelId) return true
  if (mode === 'session') return false
  return !prompt.trim() || !blueprintId
}

/**
 * `stepConfigOverrides` for `implementations.startAdHoc`.
 *
 * Auto-accepting the plan is the default server behaviour, so it is expressed as
 * the *absence* of an override; only opting out sends `awaitUserApproval`.
 */
export function planStepConfigOverrides(
  autoAcceptPlan: boolean,
): Record<string, { awaitUserApproval?: boolean }> | undefined {
  return autoAcceptPlan ? undefined : { plan: { awaitUserApproval: true } }
}

/**
 * The `stepConfigOverrides` to submit for the current composer state.
 *
 * `autoAcceptPlan` is sticky: switching from a blueprint that plans to one that
 * does not only *hides* the switch, it does not reset it. Gating on the same
 * predicate that renders the switch is what keeps a left-over "off" from
 * attaching a plan override to a blueprint that has no plan step to approve.
 */
export function resolveStepConfigOverrides(
  mode: ComposerMode,
  blueprint: ComposerBlueprint | undefined,
  autoAcceptPlan: boolean,
): Record<string, { awaitUserApproval?: boolean }> | undefined {
  if (!shouldShowAutoAcceptPlan(mode, blueprint)) return undefined
  return planStepConfigOverrides(autoAcceptPlan)
}
